import React, { useEffect, useMemo, useState } from "react";
import {
  Crown, Users, DollarSign, TrendingUp, CreditCard, Gamepad2, Activity,
  Search, Settings as SettingsIcon, ShieldOff, ShieldCheck, Trash2, LogIn, Receipt, Sparkles, Coins, RotateCcw,
  BarChart3, PauseCircle, PlayCircle, XCircle, Layers, History, Eye, Plus, Wrench, Download, Wallet, Bell, Mail, Loader2, Clapperboard, Heart, Trophy,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import api, { setToken } from "@/lib/api";
import { PageHeader, StatCard, Spinner } from "@/components/Bits";
import LicenceProtection from "@/components/LicenceProtection";
import ClipsModeration from "@/components/ClipsModeration";
import SeasonalEventsPanel from "@/components/SeasonalEventsPanel";
import PlatformCompletion from "@/components/PlatformCompletion";
import IntegrationHealth from "@/components/IntegrationHealth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const ROLE_OPTIONS = ["kid", "teen", "student", "parent", "creator", "player", "educator", "admin"];
const PLAN_OPTIONS = ["free", "pro", "studio"];
const PLAN_PRICE = { free: 0, pro: 9, studio: 29 };

const planBadge = (p) => p === "studio" ? "bg-amber-500/15 text-amber-500" : p === "pro" ? "bg-[#3b9eff]/15 text-[#3b9eff]" : "bg-[hsl(var(--secondary))] text-[hsl(var(--muted-foreground))]";

export default function SuperAdmin() {
  const navigate = useNavigate();
  const [mode, setMode] = useState(() => localStorage.getItem("owner_mode") || "oversee");
  useEffect(() => { localStorage.setItem("owner_mode", mode); }, [mode]);
  const [overview, setOverview] = useState(null);
  const [users, setUsers] = useState([]);
  const [subs, setSubs] = useState([]);
  const [txns, setTxns] = useState([]);
  const [settings, setSettings] = useState(null);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [savingUser, setSavingUser] = useState(false);
  const [cineNotifying, setCineNotifying] = useState(false);
  const [billing, setBilling] = useState(null);
  const [grantAmt, setGrantAmt] = useState("");
  const [grantReason, setGrantReason] = useState("");

  const openBilling = async (u) => {
    try { const { data } = await api.get(`/superadmin/users/${u.id}/billing`); setBilling(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not load billing"); }
  };
  const adjustCredits = async (sign) => {
    const amount = Math.abs(Number(grantAmt) || 0) * sign;
    if (!amount) { toast.error("Enter a credit amount"); return; }
    try {
      await api.post(`/superadmin/users/${billing.user.id}/credits`, { amount, reason: grantReason });
      toast.success(`${amount >= 0 ? "Granted" : "Reversed"} ${Math.abs(amount)} credits`);
      setGrantAmt(""); setGrantReason(""); openBilling(billing.user); loadUsers(q);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const loadOverview = () => api.get("/superadmin/overview").then((r) => setOverview(r.data));
  const loadUsers = (query = "") => api.get(`/superadmin/users${query ? `?q=${encodeURIComponent(query)}` : ""}`).then((r) => setUsers(r.data));
  const loadBilling = () => {
    api.get("/superadmin/billing/subscribers").then((r) => setSubs(r.data));
    api.get("/superadmin/billing/transactions").then((r) => setTxns(r.data));
  };
  const loadSettings = () => api.get("/superadmin/settings").then((r) => setSettings(r.data));

  useEffect(() => { loadOverview(); loadUsers(); loadBilling(); loadSettings(); }, []);

  const searchUsers = (e) => { e.preventDefault(); loadUsers(q); };

  const saveUser = async (patch) => {
    if (!editing) return;
    setSavingUser(true);
    try {
      const { data } = await api.patch(`/superadmin/users/${editing.id}`, patch);
      toast.success("User updated");
      setEditing(data);
      loadUsers(q); loadOverview(); loadBilling();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    } finally { setSavingUser(false); }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Delete ${u.name}? This cannot be undone.`)) return;
    try {
      await api.delete(`/superadmin/users/${u.id}`);
      toast.success("User deleted");
      setEditing(null); loadUsers(q); loadOverview();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const impersonate = async (u) => {
    if (!window.confirm(`Log in as ${u.name}? You will be switched to their account.`)) return;
    try {
      const { data } = await api.post(`/superadmin/users/${u.id}/impersonate`);
      setToken(data.token);
      toast.success(`Now viewing as ${u.name}`);
      window.location.href = "/app";
    } catch (e) { toast.error(e.response?.data?.detail || "Could not impersonate"); }
  };

  const saveSettings = async (patch) => {
    const next = { ...settings, ...patch };
    setSettings(next);
    try { await api.patch("/superadmin/settings", patch); toast.success("Settings saved"); }
    catch { toast.error("Could not save settings"); }
  };

  const notifyCinematic = async () => {
    setCineNotifying(true);
    try {
      const { data } = await api.post("/cinematic/notify");
      if (!data.email_configured) toast.warning(`${data.queued} player(s) queued — add RESEND_API_KEY to actually send. Demand is kept for later.`);
      else toast.success(`Sent to ${data.sent} of ${data.queued} interested player(s)! 🎬`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not notify players"); }
    finally { setCineNotifying(false); }
  };

  if (!overview || !settings) return <Spinner />;

  return (
    <div className="p-5 lg:p-8 max-w-6xl mx-auto" data-testid="superadmin-page">
      <PageHeader title="Owner Console" icon={Crown} subtitle="Full platform oversight — users, billing and operations." />

      <div className="inline-flex rounded-full border border-[hsl(var(--border))] p-1 mb-6" data-testid="owner-mode-switch">
        {[["oversee", "Oversee operation", Eye], ["create", "Create games (free)", Sparkles]].map(([k, label, Icon]) => (
          <button key={k} onClick={() => setMode(k)} data-testid={`owner-mode-${k}`}
            className={`flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${mode === k ? "bg-[hsl(var(--primary))] text-white" : "text-[hsl(var(--muted-foreground))]"}`}>
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>

      {mode === "create" && <OwnerCreate navigate={navigate} />}

      {mode === "oversee" && (<>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <StatCard label="Total users" value={overview.total_users} icon={Users} delay={1} />
        <StatCard label="Paying users" value={overview.paying_users} icon={CreditCard} accent="#10b981" delay={2} />
        <StatCard label="MRR" value={`$${overview.mrr}`} icon={TrendingUp} accent="#a855f7" delay={3} />
        <StatCard label="Total revenue" value={`$${overview.total_revenue}`} icon={DollarSign} accent="#f59e0b" delay={4} />
        <StatCard label="Active subs" value={overview.active_subscriptions} icon={Receipt} accent="#10b981" delay={1} />
        <StatCard label="Revenue (30d)" value={`$${overview.revenue_30d}`} icon={DollarSign} accent="#f59e0b" delay={2} />
        <StatCard label="Games" value={overview.games_total} icon={Gamepad2} accent="#3b9eff" delay={3} />
        <StatCard label="AI calls" value={overview.ai_calls} icon={Activity} accent="#ef4444" delay={4} />
      </div>

      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview" data-testid="sa-tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="analytics" data-testid="sa-tab-analytics">Analytics</TabsTrigger>
          <TabsTrigger value="completion" data-testid="sa-tab-completion">Completion</TabsTrigger>
          <TabsTrigger value="users" data-testid="sa-tab-users">Users</TabsTrigger>
          <TabsTrigger value="billing" data-testid="sa-tab-billing">Billing</TabsTrigger>
          <TabsTrigger value="money" data-testid="sa-tab-money">Money</TabsTrigger>
          <TabsTrigger value="settings" data-testid="sa-tab-settings">Settings</TabsTrigger>
          <TabsTrigger value="monetization" data-testid="sa-tab-monetization">Monetisation</TabsTrigger>
          <TabsTrigger value="protection" data-testid="sa-tab-protection">Protection</TabsTrigger>
          <TabsTrigger value="clips" data-testid="sa-tab-clips">Clips</TabsTrigger>
          <TabsTrigger value="events" data-testid="sa-tab-events">Events</TabsTrigger>
          <TabsTrigger value="leads" data-testid="sa-tab-leads">Leads</TabsTrigger>
        </TabsList>

        {/* Analytics */}
        <TabsContent value="analytics" className="mt-4">
          <SuperAdminAnalytics />
        </TabsContent>

        {/* Platform Completion audit */}
        <TabsContent value="completion" className="mt-4">
          <IntegrationHealth />
          <PlatformCompletion />
        </TabsContent>

        {/* Overview */}
        <TabsContent value="overview" className="mt-4">
          <IntegrationHealth compact />
          <div className="grid sm:grid-cols-2 gap-4">
            <Panel title="Users by role">
              {Object.entries(overview.users_by_role).map(([k, v]) => (
                <Bar key={k} label={k} value={v} max={overview.total_users} />
              ))}
            </Panel>
            <Panel title="Users by plan">
              {Object.entries(overview.users_by_plan).map(([k, v]) => (
                <Bar key={k} label={k} value={v} max={overview.total_users} accent="#10b981" />
              ))}
            </Panel>
            <Panel title="Revenue snapshot">
              <Row label="Monthly recurring (MRR)" value={`$${overview.mrr}`} />
              <Row label="Annual run-rate (ARR)" value={`$${overview.arr}`} />
              <Row label="Total collected" value={`$${overview.total_revenue}`} />
              <Row label="Last 30 days" value={`$${overview.revenue_30d}`} />
              <Row label="Paid transactions" value={overview.paid_transactions} />
            </Panel>
            <Panel title="Platform health">
              <Row label="Suspended users" value={overview.suspended_users} />
              <Row label="Games published" value={overview.games_published} />
              <Row label="Total transactions" value={overview.transactions} />
              <Row label="Signups" value={settings.signups_enabled ? "Open" : "Closed"} />
              <Row label="Maintenance mode" value={settings.maintenance_mode ? "ON" : "Off"} />
            </Panel>
          </div>
        </TabsContent>

        {/* Users */}
        <TabsContent value="users" className="mt-4">
          <form onSubmit={searchUsers} className="flex gap-2 mb-4">
            <div className="relative flex-1 max-w-md">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))]" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email or username…"
                className="pl-9" data-testid="sa-user-search" />
            </div>
            <Button type="submit" variant="outline" className="rounded-full">Search</Button>
          </form>
          <div className="rounded-2xl border border-[hsl(var(--border))] overflow-hidden">
            {users.map((u, i) => (
              <div key={u.id} className={`flex items-center gap-3 px-4 py-3 ${i % 2 ? "bg-[hsl(var(--secondary))]/30" : ""}`} data-testid={`sa-user-row-${u.id}`}>
                <span className="text-xl">{u.avatar_emoji || "🎮"}</span>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">{u.name}</div>
                  <div className="text-xs text-[hsl(var(--muted-foreground))] truncate">{u.email}</div>
                </div>
                <Badge variant="secondary" className="capitalize">{u.role}</Badge>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold capitalize ${planBadge(u.plan)}`}>{u.plan}</span>
                {u.status === "suspended" && <Badge className="bg-red-500/15 text-red-500">suspended</Badge>}
                <Button size="sm" variant="outline" className="rounded-full" onClick={() => setEditing(u)} data-testid={`sa-manage-${u.id}`}>Manage</Button>
              </div>
            ))}
            {users.length === 0 && <div className="p-8 text-center text-sm text-[hsl(var(--muted-foreground))]">No users found.</div>}
          </div>
        </TabsContent>

        {/* Billing */}
        <TabsContent value="billing" className="mt-4 space-y-5">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <StatCard label="MRR" value={`$${overview.mrr}`} icon={TrendingUp} accent="#a855f7" />
            <StatCard label="ARR" value={`$${overview.arr}`} icon={TrendingUp} accent="#10b981" />
            <StatCard label="Total revenue" value={`$${overview.total_revenue}`} icon={DollarSign} accent="#f59e0b" />
            <StatCard label="Active subs" value={overview.active_subscriptions} icon={Receipt} accent="#3b9eff" />
          </div>

          <Panel title={`Subscribers (${subs.length})`}>
            {subs.length === 0 && <p className="text-sm text-[hsl(var(--muted-foreground))]">No subscribers yet.</p>}
            {subs.map((s) => (
              <div key={s.user_id} className="flex items-center gap-3 py-2 border-b border-[hsl(var(--border))] last:border-0 text-sm">
                <div className="flex-1 min-w-0"><div className="font-semibold truncate">{s.user_name || s.user_email}</div><div className="text-xs text-[hsl(var(--muted-foreground))] truncate">{s.user_email}</div></div>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold capitalize ${planBadge(s.plan)}`}>{s.plan}</span>
                <Badge variant={s.status === "active" ? "default" : "secondary"} className="capitalize">{s.status}</Badge>
                <span className="text-xs text-[hsl(var(--muted-foreground))]">${PLAN_PRICE[s.plan] ?? 0}/mo</span>
              </div>
            ))}
          </Panel>

          <Panel title={`Transactions (${txns.length})`}>
            {txns.length === 0 && <p className="text-sm text-[hsl(var(--muted-foreground))]">No transactions yet.</p>}
            {txns.slice(0, 50).map((t) => (
              <div key={t.session_id} className="flex items-center gap-3 py-2 border-b border-[hsl(var(--border))] last:border-0 text-sm">
                <div className="flex-1 min-w-0"><div className="font-semibold truncate">{t.user_email}</div><div className="text-xs text-[hsl(var(--muted-foreground))]">{(t.created_at || "").slice(0, 16).replace("T", " ")}</div></div>
                <span className="capitalize text-xs">{t.plan_name || t.plan_id}</span>
                <span className="font-bold">${t.amount}</span>
                <Badge className={t.payment_status === "paid" ? "bg-emerald-500/15 text-emerald-500" : "bg-amber-500/15 text-amber-500"}>{t.payment_status}</Badge>
              </div>
            ))}
          </Panel>
        </TabsContent>

        {/* Settings */}
        <TabsContent value="money" className="mt-4">
          <MoneyCentre />
        </TabsContent>
        <TabsContent value="settings" className="mt-4">
          <Panel title="Platform controls">
            <Toggle label="Allow new sign-ups" desc="Turn off to pause public registration." checked={settings.signups_enabled} onCheckedChange={(v) => saveSettings({ signups_enabled: v })} testid="sa-set-signups" />
            <Toggle label="Maintenance mode" desc="Display a maintenance banner to all users." checked={settings.maintenance_mode} onCheckedChange={(v) => saveSettings({ maintenance_mode: v })} testid="sa-set-maintenance" />
            <Toggle label="Public Arcade" desc="Show the public Arcade to everyone." checked={settings.public_arcade} onCheckedChange={(v) => saveSettings({ public_arcade: v })} testid="sa-set-arcade" />
            <Toggle label="AI features enabled" desc="Globally enable AI game building & assets." checked={settings.ai_enabled} onCheckedChange={(v) => saveSettings({ ai_enabled: v })} testid="sa-set-ai" />
            <Toggle label="Cinematic Mode teaser" desc={`Show a "coming soon" AAA Unreal-stream button on games to gauge demand. ${settings.cinematic_interest_count || 0} player(s) interested so far.`} checked={settings.cinematic_teaser} onCheckedChange={(v) => saveSettings({ cinematic_teaser: v })} testid="sa-set-cinematic" />
            <div className="pt-1 pb-2">
              <Button size="sm" variant="outline" onClick={notifyCinematic} disabled={cineNotifying || !(settings.cinematic_interest_count > 0)}
                className="rounded-full gap-1.5 border-amber-400/50 text-amber-300 hover:bg-amber-400/10" data-testid="sa-cinematic-notify">
                {cineNotifying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Bell className="w-3.5 h-3.5" />}
                Notify interested players ({settings.cinematic_interest_count || 0})
              </Button>
              <p className="text-[11px] text-[hsl(var(--muted-foreground))] mt-1">Emails everyone who tapped "I want this" that Cinematic Mode is live. Needs Resend configured; otherwise demand is kept for later.</p>
            </div>
            <div className="pt-3">
              <div className="text-sm font-semibold mb-1.5 flex items-center gap-2"><Clapperboard className="w-4 h-4 text-amber-400" /> Cinematic stream URL (Pixel Streaming)</div>
              <Input defaultValue={settings.cinematic_url || ""} onBlur={(e) => saveSettings({ cinematic_url: e.target.value.trim() })}
                placeholder="https://connector.eagle3dstreaming.com/v5/…" data-testid="sa-cinematic-url" />
              <p className="text-[11px] text-[hsl(var(--muted-foreground))] mt-1">
                Paste an Eagle3D / WebRTC player URL to make Cinematic Mode <b>live</b> instantly (overrides the .env value). Leave blank to disable. {settings.cinematic_url ? "🟢 Live now." : ""}
              </p>
              <Input defaultValue={settings.cinematic_token || ""} onBlur={(e) => saveSettings({ cinematic_token: e.target.value.trim() })}
                placeholder="Optional access token" className="mt-2" data-testid="sa-cinematic-token" />
              <div className="grid grid-cols-3 gap-2 mt-3">
                <div>
                  <label className="text-[11px] text-[hsl(var(--muted-foreground))]">Session limit (min · 0 = unlimited)</label>
                  <Input type="number" min="0" defaultValue={settings.cinematic_session_limit_min || 0}
                    onBlur={(e) => saveSettings({ cinematic_session_limit_min: Math.max(0, parseInt(e.target.value || "0", 10)) })}
                    data-testid="sa-cinematic-session-limit" />
                </div>
                <div>
                  <label className="text-[11px] text-[hsl(var(--muted-foreground))]">GPU cost / hour ($)</label>
                  <Input type="number" min="0" step="0.01" defaultValue={settings.cinematic_cost_per_hour || 0}
                    onBlur={(e) => saveSettings({ cinematic_cost_per_hour: Math.max(0, parseFloat(e.target.value || "0")) })}
                    data-testid="sa-cinematic-cost" />
                </div>
                <div>
                  <label className="text-[11px] text-[hsl(var(--muted-foreground))]">Monthly budget cap ($ · 0 = off)</label>
                  <Input type="number" min="0" step="1" defaultValue={settings.cinematic_budget_cap || 0}
                    onBlur={(e) => saveSettings({ cinematic_budget_cap: Math.max(0, parseFloat(e.target.value || "0")) })}
                    data-testid="sa-cinematic-budget-cap" />
                </div>
              </div>
              <p className="text-[11px] text-[hsl(var(--muted-foreground))] mt-1" data-testid="sa-cinematic-cost-note">
                {settings.cinematic_session_limit_min > 0
                  ? `Streams auto-stop after ${settings.cinematic_session_limit_min} min to cap GPU cost`
                  : "Streams run unlimited (watch GPU cost)"}
                {settings.cinematic_cost_per_hour > 0 && settings.cinematic_session_limit_min > 0
                  ? ` — ≈ $${((settings.cinematic_cost_per_hour * settings.cinematic_session_limit_min) / 60).toFixed(2)} per session.`
                  : "."}
                {settings.cinematic_budget_cap > 0
                  ? ` Cinematic Mode auto-pauses to the teaser once est. spend hits $${Number(settings.cinematic_budget_cap).toFixed(0)}/mo.`
                  : ""}
              </p>
            </div>
            <div className="pt-3">
              <div className="text-sm font-semibold mb-1.5 flex items-center gap-2"><Sparkles className="w-4 h-4 text-[hsl(var(--primary))]" /> Global announcement</div>
              <Textarea defaultValue={settings.announcement} onBlur={(e) => saveSettings({ announcement: e.target.value })}
                placeholder="Shown to all users (leave blank for none)…" data-testid="sa-announcement" />
            </div>
          </Panel>
          <WeeklyChallengesEditor />
        </TabsContent>
        <TabsContent value="monetization" className="mt-4">
          <MonetizationTab />
        </TabsContent>
        <TabsContent value="protection" className="mt-4">
          <LicenceProtection />
        </TabsContent>
        <TabsContent value="clips" className="mt-4">
          <ClipsModeration />
        </TabsContent>
        <TabsContent value="events" className="mt-4">
          <SeasonalEventsPanel />
        </TabsContent>
        <TabsContent value="leads" className="mt-4">
          <LeadsInbox />
        </TabsContent>
      </Tabs>
      </>)}

      {/* Manage user dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="sa-manage-dialog">
          {editing && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2"><span className="text-2xl">{editing.avatar_emoji || "🎮"}</span> {editing.name}</DialogTitle>
                <DialogDescription>{editing.email}</DialogDescription>
              </DialogHeader>

              <div className="space-y-4 py-2">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-semibold text-[hsl(var(--muted-foreground))]">Role</label>
                    <Select value={editing.role} onValueChange={(v) => saveUser({ role: v })}>
                      <SelectTrigger className="mt-1" data-testid="sa-edit-role"><SelectValue /></SelectTrigger>
                      <SelectContent>{ROLE_OPTIONS.map((r) => <SelectItem key={r} value={r} className="capitalize">{r}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-[hsl(var(--muted-foreground))]">Plan</label>
                    <Select value={editing.plan} onValueChange={(v) => saveUser({ plan: v })}>
                      <SelectTrigger className="mt-1" data-testid="sa-edit-plan"><SelectValue /></SelectTrigger>
                      <SelectContent>{PLAN_OPTIONS.map((p) => <SelectItem key={p} value={p} className="capitalize">{p}{p !== "free" ? ` ($${PLAN_PRICE[p]}/mo comp)` : ""}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" className="rounded-full" onClick={() => saveUser({ add_credits: 1000 })} disabled={savingUser} data-testid="sa-add-credits">
                    <Sparkles className="w-3.5 h-3.5" /> +1000 AI credits ({editing.ai_credits})
                  </Button>
                </div>

                <div className="flex flex-wrap gap-2 pt-2 border-t border-[hsl(var(--border))]">
                  {editing.status === "suspended" ? (
                    <Button size="sm" className="rounded-full bg-emerald-500 hover:bg-emerald-600" onClick={() => saveUser({ status: "active" })} data-testid="sa-activate">
                      <ShieldCheck className="w-4 h-4" /> Reactivate
                    </Button>
                  ) : (
                    <Button size="sm" variant="outline" className="rounded-full text-amber-600" onClick={() => saveUser({ status: "suspended" })} data-testid="sa-suspend">
                      <ShieldOff className="w-4 h-4" /> Suspend
                    </Button>
                  )}
                  <Button size="sm" variant="outline" className="rounded-full" onClick={() => impersonate(editing)} data-testid="sa-impersonate">
                    <LogIn className="w-4 h-4" /> Log in as
                  </Button>
                  <Button size="sm" variant="outline" className="rounded-full" onClick={() => openBilling(editing)} data-testid="sa-billing-open">
                    <Receipt className="w-4 h-4" /> Billing
                  </Button>
                  <Button size="sm" variant="destructive" className="rounded-full ml-auto" onClick={() => deleteUser(editing)} data-testid="sa-delete">
                    <Trash2 className="w-4 h-4" /> Delete
                  </Button>
                </div>
              </div>

              <DialogFooter>
                <Button variant="ghost" onClick={() => setEditing(null)}>Close</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
      {/* Billing investigation dialog */}
      <Dialog open={!!billing} onOpenChange={(o) => !o && setBilling(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="sa-billing-dialog">
          {billing && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2"><Receipt className="w-5 h-5 text-[hsl(var(--primary))]" /> Billing — {billing.user.name}</DialogTitle>
                <DialogDescription>{billing.user.email} · {billing.user.plan} plan · joined {(billing.user.created_at || "").slice(0, 10)}</DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 mb-3" data-testid="sa-billing-credits">
                <Mini label="Remaining" value={billing.credits.remaining.toLocaleString()} />
                <Mini label="Allotment" value={billing.credits.allotment.toLocaleString()} />
                <Mini label="Bonus" value={billing.credits.bonus.toLocaleString()} />
                <Mini label="Used" value={billing.credits.used.toLocaleString()} />
                <Mini label="Lifetime spend" value={`$${billing.lifetime_spend}`} />
                <Mini label="Games" value={billing.games_created} />
                <Mini label="Assets" value={billing.assets_generated} />
                <Mini label="Renewal" value={billing.renewal_date ? billing.renewal_date.slice(0, 10) : "—"} />
              </div>

              <div className="rounded-xl border border-[hsl(var(--border))] p-3 mb-3">
                <div className="font-semibold text-sm mb-2 flex items-center gap-1.5"><Coins className="w-4 h-4" /> Grant / reverse credits</div>
                <div className="flex flex-wrap gap-2">
                  <Input type="number" value={grantAmt} onChange={(e) => setGrantAmt(e.target.value)} placeholder="Amount" className="w-28" data-testid="sa-grant-amount" />
                  <Input value={grantReason} onChange={(e) => setGrantReason(e.target.value)} placeholder="Reason (e.g. goodwill, reissue)" className="flex-1 min-w-[140px]" data-testid="sa-grant-reason" />
                  <Button size="sm" className="rounded-full bg-emerald-500 hover:bg-emerald-600" onClick={() => adjustCredits(1)} data-testid="sa-grant-credits"><Coins className="w-4 h-4" /> Grant</Button>
                  <Button size="sm" variant="outline" className="rounded-full" onClick={() => adjustCredits(-1)} data-testid="sa-reverse-credits"><RotateCcw className="w-4 h-4" /> Reverse</Button>
                </div>
              </div>

              <Panel title={`Payment history (${billing.payment_history.length})`}>
                {billing.payment_history.length === 0 && <p className="text-sm text-[hsl(var(--muted-foreground))]">No payments.</p>}
                {billing.payment_history.slice(0, 20).map((t) => (
                  <div key={t.session_id || t.id} className="flex items-center gap-2 py-1.5 text-sm border-b border-[hsl(var(--border))] last:border-0">
                    <span className="flex-1 truncate">{t.plan_name || t.plan_id || t.kind}</span>
                    <span className="font-bold">${t.amount}</span>
                    <Badge className={t.payment_status === "paid" ? "bg-emerald-500/15 text-emerald-500" : "bg-amber-500/15 text-amber-500"}>{t.payment_status}</Badge>
                  </div>
                ))}
              </Panel>

              <div className="mt-3">
                <Panel title={`Credit ledger (${billing.credit_transactions.length})`}>
                  {billing.credit_transactions.length === 0 && <p className="text-sm text-[hsl(var(--muted-foreground))]">No credit transactions.</p>}
                  {billing.credit_transactions.slice(0, 15).map((t) => (
                    <div key={t.id} className="flex items-center gap-2 py-1.5 text-sm border-b border-[hsl(var(--border))] last:border-0">
                      <span className="capitalize font-medium">{t.type}</span>
                      <span className="flex-1 truncate text-xs text-[hsl(var(--muted-foreground))]">{t.reason}</span>
                      <span className={`font-bold ${t.amount >= 0 ? "text-emerald-500" : "text-red-500"}`}>{t.amount >= 0 ? "+" : ""}{t.amount}</span>
                    </div>
                  ))}
                </Panel>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Mini = ({ label, value }) => (
  <div className="rounded-xl border border-[hsl(var(--border))] p-2.5 text-center">
    <div className="font-display text-lg font-extrabold">{value}</div>
    <div className="text-[10px] text-[hsl(var(--muted-foreground))]">{label}</div>
  </div>
);

function downloadCSV(filename, headers, rows) {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const lines = [headers.map(esc).join(",")].concat(rows.map((r) => r.map(esc).join(",")));
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

function LeadsInbox() {
  const [school, setSchool] = useState(null);
  const [waitlist, setWaitlist] = useState(null);
  useEffect(() => {
    api.get("/leads/school").then((r) => setSchool(r.data.leads || [])).catch(() => setSchool([]));
    api.get("/waitlist/founding/list").then((r) => setWaitlist(r.data.waitlist || [])).catch(() => setWaitlist([]));
  }, []);
  const fmt = (s) => (s || "").slice(0, 16).replace("T", " ");

  const exportSchool = () => downloadCSV("school-demo-requests.csv",
    ["School", "Contact", "Email", "Role", "Students", "Country", "Message", "Status", "Created"],
    (school || []).map((l) => [l.school_name, l.contact_name, l.email, l.role, l.students, l.country, l.message, l.status, l.created_at]));
  const exportWaitlist = () => downloadCSV("founding-waitlist.csv",
    ["#", "Email", "Role", "Created"],
    (waitlist || []).map((w) => [w.founding_number ?? "", w.email, w.role || "", w.created_at]));

  return (
    <div className="space-y-5" data-testid="sa-leads-inbox">
      <Panel title={
        <div className="flex items-center justify-between gap-2 w-full">
          <span className="flex items-center gap-2"><Mail className="w-4 h-4 text-[hsl(var(--primary))]" /> School demo requests ({school?.length ?? 0})</span>
          <Button size="sm" variant="outline" className="rounded-full" disabled={!school?.length} onClick={exportSchool} data-testid="sa-export-school">
            <Download className="w-3.5 h-3.5" /> Export CSV
          </Button>
        </div>
      }>
        {school === null ? <Spinner /> : school.length === 0 ? (
          <p className="text-sm text-[hsl(var(--muted-foreground))]">No school demo requests yet. They arrive from the “Bring GameView to your school” form.</p>
        ) : (
          <div className="divide-y divide-[hsl(var(--border))]">
            {school.map((l) => (
              <div key={l.id} className="py-2.5 text-sm" data-testid={`sa-school-lead-${l.id}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold">{l.school_name}</span>
                  <span className="text-[hsl(var(--muted-foreground))]">· {l.contact_name}</span>
                  {l.role && <Badge variant="secondary" className="capitalize">{l.role}</Badge>}
                  {l.students && <span className="text-xs text-[hsl(var(--muted-foreground))]">{l.students} students</span>}
                  {l.country && <span className="text-xs text-[hsl(var(--muted-foreground))]">· {l.country}</span>}
                  <span className="ml-auto text-xs text-[hsl(var(--muted-foreground))]">{fmt(l.created_at)}</span>
                </div>
                <a href={`mailto:${l.email}`} className="text-[hsl(var(--primary))] hover:underline text-xs">{l.email}</a>
                {l.message && <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1 line-clamp-2">{l.message}</p>}
              </div>
            ))}
          </div>
        )}
      </Panel>

      <Panel title={
        <div className="flex items-center justify-between gap-2 w-full">
          <span className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-amber-400" /> Founding waitlist ({waitlist?.length ?? 0})</span>
          <Button size="sm" variant="outline" className="rounded-full" disabled={!waitlist?.length} onClick={exportWaitlist} data-testid="sa-export-waitlist">
            <Download className="w-3.5 h-3.5" /> Export CSV
          </Button>
        </div>
      }>
        {waitlist === null ? <Spinner /> : waitlist.length === 0 ? (
          <p className="text-sm text-[hsl(var(--muted-foreground))]">No founding-creator signups yet. They arrive from the “Imagine It. Build It.” waitlist on the landing page.</p>
        ) : (
          <div className="divide-y divide-[hsl(var(--border))]">
            {waitlist.map((w) => (
              <div key={w.id} className="flex items-center gap-3 py-2 text-sm" data-testid={`sa-waitlist-row-${w.id}`}>
                {w.founding_number && w.founding_number <= 100 && (
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-400/15 text-amber-500 shrink-0">Founder #{w.founding_number}</span>
                )}
                <a href={`mailto:${w.email}`} className="font-semibold text-[hsl(var(--primary))] hover:underline truncate">{w.email}</a>
                {w.role && <Badge variant="secondary" className="capitalize">{w.role}</Badge>}
                <span className="ml-auto text-xs text-[hsl(var(--muted-foreground))]">{fmt(w.created_at)}</span>
              </div>
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
}

const Panel = ({ title, children }) => (
  <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5">
    <div className="font-display font-bold mb-3">{title}</div>{children}
  </div>
);
const Row = ({ label, value }) => (
  <div className="flex items-center justify-between py-1.5 text-sm">
    <span className="text-[hsl(var(--muted-foreground))]">{label}</span><span className="font-bold">{value}</span>
  </div>
);
const Bar = ({ label, value, max, accent = "#3b9eff" }) => (
  <div className="mb-2.5">
    <div className="flex justify-between text-xs mb-1"><span className="capitalize">{label}</span><span className="font-bold">{value}</span></div>
    <div className="h-2 rounded-full bg-[hsl(var(--secondary))] overflow-hidden">
      <div className="h-full rounded-full" style={{ width: `${max ? (value / max) * 100 : 0}%`, background: accent }} />
    </div>
  </div>
);
const Toggle = ({ label, desc, checked, onCheckedChange, testid }) => (
  <div className="flex items-center justify-between py-3 border-b border-[hsl(var(--border))] last:border-0">
    <div><div className="text-sm font-semibold">{label}</div><div className="text-xs text-[hsl(var(--muted-foreground))]">{desc}</div></div>
    <Switch checked={checked} onCheckedChange={onCheckedChange} data-testid={testid} />
  </div>
);

const usd = (n) => `$${Number(n || 0).toFixed(2)}`;

function RevenueTrendChart({ series }) {
  if (!series || series.length === 0) return <p className="text-sm text-[hsl(var(--muted-foreground))]">No revenue activity in this period yet.</p>;
  const max = Math.max(...series.map((s) => s.total), 1);
  return (
    <div data-testid="sa-trend-chart">
      <div className="flex items-end h-40" style={{ gap: series.length > 60 ? "1px" : "4px" }}>
        {series.map((s) => (
          <div key={s.date} className="flex-1 flex flex-col justify-end group relative" title={`${s.date}: $${s.total.toFixed(2)}`}>
            <div className="w-full rounded-t bg-gradient-to-t from-[#1f6feb] to-[#7c3aed] transition-all hover:opacity-80" style={{ height: `${Math.max(2, (s.total / max) * 100)}%` }} />
            <div className="absolute -top-7 left-1/2 -translate-x-1/2 hidden group-hover:block whitespace-nowrap text-[10px] bg-black/80 text-white px-1.5 py-0.5 rounded">{usd(s.total)}</div>
          </div>
        ))}
      </div>
      <div className="flex justify-between text-[10px] text-[hsl(var(--muted-foreground))] mt-1.5">
        <span>{series[0]?.date}</span><span>{series[series.length - 1]?.date}</span>
      </div>
    </div>
  );
}

const WEEKLY_EVENTS = ["play", "create", "like"];
const WEEKLY_ICONS = ["trophy", "gamepad", "wand", "heart", "flame", "star"];

function SeriesBars({ series, valueKey, accent = "#3b9eff", fmt = (v) => v }) {
  const max = Math.max(1, ...series.map((s) => Number(s[valueKey]) || 0));
  return (
    <div className="flex items-end gap-[2px] h-28" data-testid="analytics-chart">
      {series.map((s, i) => {
        const v = Number(s[valueKey]) || 0;
        const h = Math.max(v > 0 ? 4 : 1, Math.round((v / max) * 100));
        return (
          <div key={i} className="flex-1 h-full flex flex-col justify-end" title={`${s.date}: ${fmt(v)}`}>
            <div className="rounded-t-sm transition-all" style={{ height: `${h}%`, backgroundColor: v > 0 ? accent : "hsl(var(--muted))", opacity: v > 0 ? 0.85 : 0.4 }} />
          </div>
        );
      })}
    </div>
  );
}

function AnalyticsTile({ label, value, sub, accent }) {
  return (
    <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4" data-testid="analytics-tile">
      <div className="text-xs text-[hsl(var(--muted-foreground))]">{label}</div>
      <div className="text-2xl font-display font-bold tabular-nums mt-0.5" style={{ color: accent }}>{value}</div>
      {sub && <div className="text-[11px] text-[hsl(var(--muted-foreground))] mt-0.5">{sub}</div>}
    </div>
  );
}

function SuperAdminAnalytics() {
  const [days, setDays] = useState(30);
  const [d, setD] = useState(null);
  const [funnel, setFunnel] = useState(null);
  useEffect(() => {
    setD(null); setFunnel(null);
    api.get("/superadmin/analytics", { params: { days } }).then((r) => setD(r.data)).catch(() => setD(false));
    api.get("/superadmin/funnel", { params: { days } }).then((r) => setFunnel(r.data)).catch(() => setFunnel(false));
  }, [days]);

  if (d === false) return <div className="text-sm text-[hsl(var(--muted-foreground))]">Couldn't load analytics.</div>;
  if (!d) return <div className="flex items-center gap-2 text-sm text-[hsl(var(--muted-foreground))]"><Loader2 className="w-4 h-4 animate-spin" /> Loading analytics…</div>;
  const s = d.summary;
  const usd = (v) => `$${Number(v || 0).toFixed(2)}`;
  const FUNNEL_COLORS = ["#3b9eff", "#a855f7", "#34d399", "#f59e0b"];

  return (
    <div className="space-y-5" data-testid="sa-analytics">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="font-display font-bold flex items-center gap-2"><BarChart3 className="w-5 h-5 text-[#3b9eff]" /> Growth Analytics</div>
        <div className="flex gap-1" data-testid="analytics-range">
          {[7, 30, 90].map((n) => (
            <Button key={n} size="sm" variant={days === n ? "default" : "outline"} className="rounded-full h-7 px-3 text-xs" onClick={() => setDays(n)} data-testid={`analytics-range-${n}`}>{n}d</Button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <AnalyticsTile label="New users" value={s.new_users} sub={`${s.active_players} active players`} accent="#3b9eff" />
        <AnalyticsTile label="Tips volume" value={usd(s.tips_total)} sub={`${s.tips_count} tips · ${s.unique_tippers} tippers`} accent="#34d399" />
        <AnalyticsTile label="Quest completion" value={`${s.quest_completion_rate}%`} sub={`${s.quest_claims} rewards claimed`} accent="#f59e0b" />
        <AnalyticsTile label="Weekly challenges" value={s.weekly_completions} sub="completed this period" accent="#a855f7" />
      </div>

      {funnel && funnel.funnel && (
        <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4" data-testid="sa-funnel">
          <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
            <div className="font-display font-bold flex items-center gap-2 text-sm"><TrendingUp className="w-4 h-4 text-[#3b9eff]" /> Activation funnel</div>
            <div className="text-xs text-[hsl(var(--muted-foreground))]">
              Primary metric — <b className="text-[hsl(var(--foreground))]" data-testid="funnel-activation-rate">{funnel.first_game_creation_rate}%</b> create their first game
            </div>
          </div>
          <div className="space-y-2.5">
            {funnel.funnel.map((step, i) => (
              <div key={step.key} className="flex items-center gap-3" data-testid={`funnel-step-${step.key}`}>
                <div className="w-36 shrink-0 text-sm">{step.label}</div>
                <div className="flex-1 h-7 rounded-lg bg-[hsl(var(--secondary))] overflow-hidden">
                  <div className="h-full rounded-lg flex items-center justify-end px-2 text-[11px] font-bold text-white transition-all"
                    style={{ width: `${Math.max(step.pct, 6)}%`, background: FUNNEL_COLORS[i % FUNNEL_COLORS.length] }}>
                    {step.pct}%
                  </div>
                </div>
                <div className="w-16 shrink-0 text-right font-display font-extrabold tabular-nums">{step.count}</div>
              </div>
            ))}
          </div>
          <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-xs text-[hsl(var(--muted-foreground))]">
            <span>Publish rate: <b className="text-[hsl(var(--foreground))]">{funnel.publish_rate}%</b></span>
            <span>7-day creator retention: <b className="text-[hsl(var(--foreground))]">{funnel.retention_7d_rate}%</b></span>
            {funnel.time_to_first_playable_hours != null && (
              <span data-testid="funnel-ttfp">Time to first playable: <b className="text-[hsl(var(--foreground))]">{
                funnel.time_to_first_playable_hours < 1
                  ? `${Math.round(funnel.time_to_first_playable_hours * 60)} min`
                  : `${funnel.time_to_first_playable_hours} h`
              }</b> <span className="opacity-70">(median, n={funnel.time_to_first_playable_sample})</span></span>
            )}
            <span>{funnel.registered} new sign-ups in window</span>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-3 gap-4">
        <Panel title="New users / day">
          <SeriesBars series={d.users_series} valueKey="count" accent="#3b9eff" />
        </Panel>
        <Panel title="Tips $ / day">
          <SeriesBars series={d.tips_series} valueKey="amount" accent="#34d399" fmt={usd} />
        </Panel>
        <Panel title="Active players / day">
          <SeriesBars series={d.active_series} valueKey="count" accent="#f59e0b" />
        </Panel>
      </div>
    </div>
  );
}

function WeeklyChallengesEditor() {
  const [rows, setRows] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [isDefault, setIsDefault] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/superadmin/weekly-challenges").then((r) => {
    setRows(r.data.challenges); setActiveId(r.data.active_id); setIsDefault(r.data.is_default);
  }).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const setRow = (i, patch) => setRows((rs) => rs.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  const addRow = () => setRows((rs) => [...rs, { id: "", event: "play", target: 10, reward: 50, title: "", desc: "", icon: "trophy" }]);
  const removeRow = (i) => setRows((rs) => rs.filter((_, idx) => idx !== i));

  const save = async (reset = false) => {
    setSaving(true);
    try {
      const payload = reset ? { challenges: [] } : { challenges: rows };
      const { data } = await api.put("/superadmin/weekly-challenges", payload);
      setRows(data.challenges); setActiveId(data.active_id); setIsDefault(data.is_default);
      toast.success(reset ? "Reset to default challenges" : "Weekly challenges saved 🏆");
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't save"); }
    finally { setSaving(false); }
  };

  if (!rows) return null;
  return (
    <div className="rounded-2xl border border-indigo-500/30 bg-gradient-to-br from-indigo-500/[0.06] to-transparent p-5 mt-4" data-testid="sa-weekly-editor">
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="font-display font-bold flex items-center gap-2"><Trophy className="w-5 h-5 text-indigo-400" /> Weekly Challenges</div>
        <Badge variant="secondary" className="text-[11px]">{isDefault ? "Using defaults" : "Custom"}</Badge>
      </div>
      <p className="text-xs text-[hsl(var(--muted-foreground))] mb-3">Author the pool of weekly challenges. The active one rotates by ISO week; the current week's pick is highlighted.</p>
      <div className="space-y-3">
        {rows.map((r, i) => (
          <div key={i} className={`rounded-xl border p-3 ${r.id && r.id === activeId ? "border-indigo-500/60 bg-indigo-500/10" : "border-[hsl(var(--border))]"}`} data-testid={`sa-weekly-row-${i}`}>
            <div className="flex items-center justify-between gap-2 mb-2">
              <div className="flex items-center gap-2 text-xs">
                {r.id && r.id === activeId && <Badge className="bg-indigo-500 text-white text-[10px]">Active this week</Badge>}
                <span className="text-[hsl(var(--muted-foreground))]">Reward: <b className="text-indigo-400">+{r.reward || 0}</b> credits</span>
              </div>
              <Button variant="ghost" size="sm" className="h-7 text-red-500 rounded-full" onClick={() => removeRow(i)} data-testid={`sa-weekly-remove-${i}`}><Trash2 className="w-3.5 h-3.5" /></Button>
            </div>
            <div className="grid sm:grid-cols-2 gap-2">
              <Input value={r.title} onChange={(e) => setRow(i, { title: e.target.value })} placeholder="Title (e.g. Marathon Week)" data-testid={`sa-weekly-title-${i}`} />
              <Input value={r.desc} onChange={(e) => setRow(i, { desc: e.target.value })} placeholder="Description" data-testid={`sa-weekly-desc-${i}`} />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
              <div>
                <span className="text-[10px] text-[hsl(var(--muted-foreground))]">Event</span>
                <select value={r.event} onChange={(e) => setRow(i, { event: e.target.value })} className="w-full mt-0.5 h-9 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-2 text-sm" data-testid={`sa-weekly-event-${i}`}>
                  {WEEKLY_EVENTS.map((ev) => <option key={ev} value={ev}>{ev}</option>)}
                </select>
              </div>
              <div>
                <span className="text-[10px] text-[hsl(var(--muted-foreground))]">Target</span>
                <Input type="number" min="1" value={r.target} onChange={(e) => setRow(i, { target: e.target.value })} className="mt-0.5" data-testid={`sa-weekly-target-${i}`} />
              </div>
              <div>
                <span className="text-[10px] text-[hsl(var(--muted-foreground))]">Reward (credits)</span>
                <Input type="number" min="0" value={r.reward} onChange={(e) => setRow(i, { reward: e.target.value })} className="mt-0.5" data-testid={`sa-weekly-reward-${i}`} />
              </div>
              <div>
                <span className="text-[10px] text-[hsl(var(--muted-foreground))]">Icon</span>
                <select value={r.icon} onChange={(e) => setRow(i, { icon: e.target.value })} className="w-full mt-0.5 h-9 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-2 text-sm" data-testid={`sa-weekly-icon-${i}`}>
                  {WEEKLY_ICONS.map((ic) => <option key={ic} value={ic}>{ic}</option>)}
                </select>
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2 mt-3 flex-wrap">
        <Button variant="outline" size="sm" className="rounded-full" onClick={addRow} data-testid="sa-weekly-add"><Plus className="w-4 h-4" /> Add challenge</Button>
        <Button size="sm" className="rounded-full bg-gradient-to-r from-indigo-500 to-violet-500 text-white" onClick={() => save(false)} disabled={saving || rows.length === 0} data-testid="sa-weekly-save">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save challenges"}</Button>
        <Button variant="ghost" size="sm" className="rounded-full" onClick={() => save(true)} disabled={saving} data-testid="sa-weekly-reset"><RotateCcw className="w-4 h-4" /> Reset to defaults</Button>
      </div>
    </div>
  );
}

function MonetizationTab() {
  const [d, setD] = useState(null);
  const [form, setForm] = useState(null);
  const [trends, setTrends] = useState(null);
  const [saving, setSaving] = useState(false);
  const [drill, setDrill] = useState(null);
  const [plans, setPlans] = useState(null);
  const [savingPlans, setSavingPlans] = useState(false);
  const [notifying, setNotifying] = useState(false);
  const [near, setNear] = useState(null);

  const load = () => api.get("/superadmin/monetization").then((r) => { setD(r.data); setForm(r.data.settings); });
  const loadNear = () => api.get("/superadmin/creators-near-payout").then((r) => setNear(r.data)).catch(() => setNear(null));
  const loadPlans = () => api.get("/superadmin/plans").then((r) => setPlans(r.data.plans)).catch(() => setPlans([]));
  useEffect(() => { load(); loadNear(); loadPlans(); api.get("/superadmin/monetization/trends?days=30").then((r) => setTrends(r.data.series)).catch(() => setTrends([])); }, []);
  if (!d || !form) return <Spinner />;
  const o = d.overview;

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/superadmin/monetization", {
        platform_fee_rate: Number(form.platform_fee_rate),
        min_payout: Number(form.min_payout),
        credit_conversion_rate: Number(form.credit_conversion_rate),
        marketplace_enabled: !!form.marketplace_enabled,
        guild_pro_price_credits: Number(form.guild_pro_price_credits),
        guild_pro_duration_days: Number(form.guild_pro_duration_days),
        guild_pro_max_members: Number(form.guild_pro_max_members),
        tip_perk_enabled: !!form.tip_perk_enabled,
        tip_perk_credits_per_dollar: Number(form.tip_perk_credits_per_dollar),
        tip_perk_max: Number(form.tip_perk_max),
      });
      setForm(data.settings); toast.success("Monetisation settings saved!"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };
  const payoutAction = async (id, action, label) => {
    try { await api.post(`/superadmin/payouts/${id}/${action}`); toast.success(label); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const notifyPayoutReady = async () => {
    setNotifying(true);
    try {
      const { data } = await api.post("/marketplace/payouts/notify/run");
      if (!data.email_configured) toast.error("Email isn't configured — set RESEND_API_KEY + SENDER_EMAIL in production.");
      else toast.success(`Reminder sent to ${data.sent} creator${data.sent === 1 ? "" : "s"} over the minimum (${data.eligible} eligible).`);
      loadNear();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send reminders"); }
    finally { setNotifying(false); }
  };
  const openDrill = async (uid) => {
    setDrill({ loading: true });
    try { const { data } = await api.get(`/superadmin/monetization/creator/${uid}`); setDrill(data); }
    catch (e) { toast.error("Couldn't load creator earnings"); setDrill(null); }
  };
  const editPlan = (id, key, value) => setPlans((ps) => ps.map((p) => p.id === id ? { ...p, [key]: value } : p));
  const editCap = (id, key, value) => setPlans((ps) => ps.map((p) => p.id === id ? { ...p, caps: { ...p.caps, [key]: value } } : p));
  const savePlans = async () => {
    setSavingPlans(true);
    try {
      const payload = { plans: {} };
      plans.forEach((p) => { payload.plans[p.id] = { credits: Number(p.credits), caps: { max_games: Number(p.caps.max_games), max_published: Number(p.caps.max_published), marketplace: !!p.caps.marketplace }, ...(p.editable_price ? { price: Number(p.price) } : {}) }; });
      const { data } = await api.put("/superadmin/plans", payload);
      setPlans(data.plans); toast.success("Plans & quotas updated!"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save plans"); }
    finally { setSavingPlans(false); }
  };

  return (
    <div className="space-y-5" data-testid="sa-monetization">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard label="Total platform revenue" value={usd(o.total_platform_revenue)} icon={DollarSign} accent="#22c55e" />
        <StatCard label="Recurring (MRR)" value={`${usd(o.mrr)} · ${o.active_subscribers} subs`} icon={RotateCcw} accent="#3b9eff" delay={1} />
        <StatCard label="Creator net earnings" value={usd(o.creator_net)} icon={Coins} accent="#a855f7" delay={2} />
        <StatCard label="Pending payouts" value={`${o.pending_payouts_count} · ${usd(o.pending_payouts_sum)}`} icon={Receipt} accent="#f59e0b" delay={3} />
      </div>

      <Panel title="Revenue (last 30 days)">
        <RevenueTrendChart series={trends} />
      </Panel>

      <div className="grid lg:grid-cols-2 gap-4">
        <Panel title="Revenue by source">
          <div className="space-y-2.5" data-testid="sa-breakdown">
            {d.breakdown.map((b) => {
              const total = d.breakdown.reduce((s, x) => s + x.amount, 0) || 1;
              return (
                <div key={b.source}>
                  <div className="flex justify-between text-sm mb-1"><span>{b.source}</span><b>{usd(b.amount)}</b></div>
                  <div className="h-2 rounded-full bg-[hsl(var(--secondary))] overflow-hidden"><div className="h-full bg-gradient-to-r from-[#1f6feb] to-[#7c3aed]" style={{ width: `${(b.amount / total) * 100}%` }} /></div>
                </div>
              );
            })}
          </div>
        </Panel>
        <Panel title="Plan distribution">
          <div className="space-y-2" data-testid="sa-plan-dist">
            {d.plan_distribution.map((p) => (
              <div key={p.plan} className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-2"><Layers className="w-4 h-4 text-[hsl(var(--muted-foreground))]" /> {p.name}</span>
                <Badge variant="secondary">{p.count} users</Badge>
              </div>
            ))}
          </div>
        </Panel>
      </div>

      <Panel title="Revenue split & wallet rules">
        <div className="space-y-4">
          <div>
            <div className="flex justify-between text-sm mb-1"><span>Platform fee (your cut of sales)</span><b data-testid="sa-fee-value">{Math.round(form.platform_fee_rate * 100)}% · creator keeps {Math.round((1 - form.platform_fee_rate) * 100)}%</b></div>
            <input type="range" min="0" max="50" value={Math.round(form.platform_fee_rate * 100)} onChange={(e) => setForm((f) => ({ ...f, platform_fee_rate: Number(e.target.value) / 100 }))} className="w-full accent-[hsl(var(--primary))]" data-testid="sa-fee-rate" />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <span className="text-xs text-[hsl(var(--muted-foreground))]">Minimum cash payout ($)</span>
              <Input type="number" value={form.min_payout} onChange={(e) => setForm((f) => ({ ...f, min_payout: e.target.value }))} className="mt-1" data-testid="sa-min-payout" />
            </div>
            <div>
              <span className="text-xs text-[hsl(var(--muted-foreground))]">Credit conversion (credits per $1)</span>
              <Input type="number" value={form.credit_conversion_rate} onChange={(e) => setForm((f) => ({ ...f, credit_conversion_rate: e.target.value }))} className="mt-1" data-testid="sa-conversion-rate" />
            </div>
          </div>
          <Toggle label="Marketplace enabled" desc="Allow creators to sell items platform-wide." checked={form.marketplace_enabled} onCheckedChange={(v) => setForm((f) => ({ ...f, marketplace_enabled: v }))} testid="sa-marketplace-enabled" />
          <div className="rounded-xl border border-amber-400/30 bg-amber-400/5 p-3" data-testid="sa-guild-pro">
            <div className="text-sm font-bold flex items-center gap-1.5 mb-2">👑 Guild Pro</div>
            <div className="grid sm:grid-cols-3 gap-3">
              <div>
                <span className="text-xs text-[hsl(var(--muted-foreground))]">Price (credits)</span>
                <Input type="number" min="0" value={form.guild_pro_price_credits ?? 500} onChange={(e) => setForm((f) => ({ ...f, guild_pro_price_credits: e.target.value }))} className="mt-1" data-testid="sa-guildpro-price" />
              </div>
              <div>
                <span className="text-xs text-[hsl(var(--muted-foreground))]">Duration (days)</span>
                <Input type="number" min="1" value={form.guild_pro_duration_days ?? 30} onChange={(e) => setForm((f) => ({ ...f, guild_pro_duration_days: e.target.value }))} className="mt-1" data-testid="sa-guildpro-days" />
              </div>
              <div>
                <span className="text-xs text-[hsl(var(--muted-foreground))]">Pro member cap</span>
                <Input type="number" min="100" value={form.guild_pro_max_members ?? 250} onChange={(e) => setForm((f) => ({ ...f, guild_pro_max_members: e.target.value }))} className="mt-1" data-testid="sa-guildpro-cap" />
              </div>
            </div>
          </div>
          <div className="rounded-xl border border-emerald-400/30 bg-emerald-400/5 p-3" data-testid="sa-tip-perk">
            <div className="text-sm font-bold flex items-center gap-1.5 mb-1">💚 Creator Tip Perk</div>
            <p className="text-xs text-[hsl(var(--muted-foreground))] mb-2">Reward players with bonus AI credits when they tip a creator — encourages repeat tipping.</p>
            <Toggle label="Tip perk enabled" desc="Grant bonus AI credits on every tip." checked={form.tip_perk_enabled ?? true} onCheckedChange={(v) => setForm((f) => ({ ...f, tip_perk_enabled: v }))} testid="sa-tipperk-enabled" />
            <div className="grid sm:grid-cols-2 gap-3 mt-2">
              <div>
                <span className="text-xs text-[hsl(var(--muted-foreground))]">Bonus credits per $1 tipped</span>
                <Input type="number" min="0" value={form.tip_perk_credits_per_dollar ?? 10} onChange={(e) => setForm((f) => ({ ...f, tip_perk_credits_per_dollar: e.target.value }))} className="mt-1" data-testid="sa-tipperk-per-dollar" />
              </div>
              <div>
                <span className="text-xs text-[hsl(var(--muted-foreground))]">Max bonus credits per tip</span>
                <Input type="number" min="0" value={form.tip_perk_max ?? 200} onChange={(e) => setForm((f) => ({ ...f, tip_perk_max: e.target.value }))} className="mt-1" data-testid="sa-tipperk-max" />
              </div>
            </div>
          </div>
          <Button onClick={save} disabled={saving} className="rounded-full bg-gradient-to-r from-[#1f6feb] to-[#3b9eff]" data-testid="sa-save-monetization">{saving ? "Saving…" : "Save monetisation settings"}</Button>
        </div>
      </Panel>

      <Panel title="Plans & quotas">
        <p className="text-xs text-[hsl(var(--muted-foreground))] mb-3">Set each tier's monthly price and AI-credit allowance. Changes apply to new checkouts, plan grants and renewals instantly.</p>
        {!plans ? <Spinner /> : (
          <div className="space-y-3" data-testid="sa-plans-editor">
            <div className="hidden sm:grid grid-cols-[1.4fr_1fr_1fr_1fr] gap-3 text-[11px] uppercase tracking-wide text-[hsl(var(--muted-foreground))] px-1">
              <span>Plan</span><span>Price / mo ($)</span><span>Monthly AI credits</span><span className="text-right">Active</span>
            </div>
            {plans.map((p) => (
              <div key={p.id} className="rounded-xl border border-[hsl(var(--border))] p-3 space-y-3" data-testid={`sa-plan-${p.id}`}>
                <div className="grid sm:grid-cols-[1.4fr_1fr_1fr_1fr] grid-cols-2 gap-3 items-center">
                  <div className="flex items-center gap-2 col-span-2 sm:col-span-1">
                    <Badge className={planBadge(p.id)}>{p.name}</Badge>
                    <span className="text-xs text-[hsl(var(--muted-foreground))]">{p.users} users</span>
                  </div>
                  <div>
                    <span className="sm:hidden text-[11px] text-[hsl(var(--muted-foreground))]">Price / mo ($)</span>
                    <Input type="number" min="0" step="1" value={p.price} disabled={!p.editable_price}
                      onChange={(e) => editPlan(p.id, "price", e.target.value)} className="h-9" data-testid={`sa-plan-price-${p.id}`} />
                  </div>
                  <div>
                    <span className="sm:hidden text-[11px] text-[hsl(var(--muted-foreground))]">Monthly AI credits</span>
                    <Input type="number" min="0" step="50" value={p.credits}
                      onChange={(e) => editPlan(p.id, "credits", e.target.value)} className="h-9" data-testid={`sa-plan-credits-${p.id}`} />
                  </div>
                  <div className="text-right text-sm col-span-2 sm:col-span-1"><b>{p.active_subscribers}</b> <span className="text-xs text-[hsl(var(--muted-foreground))]">subs</span></div>
                </div>
                <div className="grid sm:grid-cols-3 gap-3 pt-1 border-t border-[hsl(var(--border))]">
                  <div>
                    <span className="text-[11px] text-[hsl(var(--muted-foreground))]">Max games (−1 = ∞)</span>
                    <Input type="number" min="-1" step="1" value={p.caps.max_games}
                      onChange={(e) => editCap(p.id, "max_games", e.target.value)} className="h-9 mt-1" data-testid={`sa-plan-maxgames-${p.id}`} />
                  </div>
                  <div>
                    <span className="text-[11px] text-[hsl(var(--muted-foreground))]">Max published (−1 = ∞)</span>
                    <Input type="number" min="-1" step="1" value={p.caps.max_published}
                      onChange={(e) => editCap(p.id, "max_published", e.target.value)} className="h-9 mt-1" data-testid={`sa-plan-maxpub-${p.id}`} />
                  </div>
                  <div className="flex items-center justify-between sm:justify-start sm:gap-3 pt-4">
                    <span className="text-sm">Marketplace selling</span>
                    <Switch checked={!!p.caps.marketplace} onCheckedChange={(v) => editCap(p.id, "marketplace", v)} data-testid={`sa-plan-market-${p.id}`} />
                  </div>
                </div>
              </div>
            ))}
            <Button onClick={savePlans} disabled={savingPlans} className="rounded-full bg-gradient-to-r from-[#7c3aed] to-[#a855f7]" data-testid="sa-save-plans">{savingPlans ? "Saving…" : "Save plans & quotas"}</Button>
          </div>
        )}
      </Panel>

      {near && near.creators && near.creators.length > 0 && (
        <Panel title={`Creators near payout (${near.creators.length})`}>
          <p className="text-xs text-[hsl(var(--muted-foreground))] mb-3">
            {near.over_count} creator{near.over_count === 1 ? "" : "s"} over the {usd(near.min_payout)} minimum · sorted by balance. Click a name to view their earnings.
          </p>
          <div className="divide-y divide-[hsl(var(--border))]" data-testid="sa-creators-near-payout">
            {near.creators.map((c) => (
              <button key={c.id} onClick={() => openDrill(c.id)} data-testid={`sa-near-${c.id}`}
                className="w-full flex items-center gap-3 py-2.5 text-left hover:bg-[hsl(var(--muted))]/40 rounded-lg px-1 transition-colors">
                <div className="text-2xl shrink-0" aria-hidden>{c.avatar_emoji}</div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold truncate">{c.name}</span>
                    {c.over
                      ? <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 shrink-0">Ready</span>
                      : <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-500/20 text-amber-300 shrink-0">{Math.round(c.pct)}%</span>}
                  </div>
                  <div className="mt-1 h-1.5 rounded-full bg-[hsl(var(--muted))] overflow-hidden">
                    <div className={`h-full rounded-full ${c.over ? "bg-emerald-500" : "bg-amber-500"}`} style={{ width: `${c.pct}%` }} />
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-bold tabular-nums">{usd(c.available)}</div>
                  {c.tips > 0 && <div className="text-[10px] text-emerald-400">incl. {usd(c.tips)} tips</div>}
                </div>
              </button>
            ))}
          </div>
        </Panel>
      )}

      <Panel title={`Pending payouts (${d.pending_payouts.length})`}>
        <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
          <p className="text-xs text-[hsl(var(--muted-foreground))]">Nudge creators whose withdrawable balance (sales + tips) is over the {usd(form.min_payout)} minimum to cash out — once per creator per week.</p>
          <Button size="sm" variant="outline" onClick={notifyPayoutReady} disabled={notifying}
            className="rounded-full gap-1.5 border-emerald-400/50 text-emerald-300 hover:bg-emerald-400/10 shrink-0" data-testid="sa-payout-notify">
            {notifying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
            Email creators over the minimum
          </Button>
        </div>
        {d.pending_payouts.length === 0 ? <p className="text-sm text-[hsl(var(--muted-foreground))]">No pending payouts.</p> :
          <div className="divide-y divide-[hsl(var(--border))]">
            {d.pending_payouts.map((p) => (
              <div key={p.id} className="flex items-center justify-between py-2.5 text-sm gap-2" data-testid={`sa-payout-${p.id}`}>
                <div className="min-w-0"><div className="font-semibold">{usd(p.amount)} · {p.method}</div><div className="text-xs text-[hsl(var(--muted-foreground))] truncate">{p.destination}</div></div>
                <div className="flex gap-1.5 shrink-0">
                  <Button size="sm" variant="outline" className="rounded-full" onClick={() => payoutAction(p.id, "mark-paid", "Marked as paid")} data-testid={`sa-markpaid-${p.id}`}>Mark paid</Button>
                  <Button size="sm" variant="outline" className="rounded-full" onClick={() => payoutAction(p.id, "hold", "Payout held")} data-testid={`sa-hold-${p.id}`}><PauseCircle className="w-3.5 h-3.5" /> Hold</Button>
                  <Button size="sm" variant="outline" className="rounded-full text-[hsl(var(--destructive))]" onClick={() => payoutAction(p.id, "reject", "Payout rejected — balance returned")} data-testid={`sa-reject-${p.id}`}><XCircle className="w-3.5 h-3.5" /> Reject</Button>
                </div>
              </div>
            ))}
          </div>}
      </Panel>

      {d.held_payouts.length > 0 && (
        <Panel title={`Held payouts (${d.held_payouts.length})`}>
          <div className="divide-y divide-[hsl(var(--border))]">
            {d.held_payouts.map((p) => (
              <div key={p.id} className="flex items-center justify-between py-2.5 text-sm gap-2" data-testid={`sa-held-${p.id}`}>
                <div className="min-w-0"><div className="font-semibold">{usd(p.amount)} · {p.method}</div><div className="text-xs text-[hsl(var(--muted-foreground))] truncate">{p.destination}</div></div>
                <div className="flex gap-1.5 shrink-0">
                  <Button size="sm" variant="outline" className="rounded-full" onClick={() => payoutAction(p.id, "unhold", "Payout released to pending")} data-testid={`sa-unhold-${p.id}`}><PlayCircle className="w-3.5 h-3.5" /> Release</Button>
                  <Button size="sm" variant="outline" className="rounded-full text-[hsl(var(--destructive))]" onClick={() => payoutAction(p.id, "reject", "Payout rejected — balance returned")} data-testid={`sa-reject-held-${p.id}`}><XCircle className="w-3.5 h-3.5" /> Reject</Button>
                </div>
              </div>
            ))}
          </div>
        </Panel>
      )}

      <Panel title="Top earning creators">
        {d.top_earners.length === 0 ? <p className="text-sm text-[hsl(var(--muted-foreground))]">No sales yet.</p> :
          <div className="divide-y divide-[hsl(var(--border))]">
            {d.top_earners.map((t, i) => (
              <button key={t.id} onClick={() => openDrill(t.id)} className="w-full flex items-center justify-between py-2 text-sm hover:bg-[hsl(var(--secondary))] rounded-lg px-2 -mx-2 transition-colors" data-testid={`sa-earner-${t.id}`}>
                <span className="flex items-center gap-2"><b className="w-5 text-[hsl(var(--muted-foreground))]">{i + 1}</b> {t.name}</span>
                <span className="font-semibold">{usd(t.gross)} <span className="text-xs text-[hsl(var(--muted-foreground))]">({t.sales} sales)</span></span>
              </button>
            ))}
          </div>}
      </Panel>

      {d.payout_history.length > 0 && (
        <Panel title="Payout history">
          <div className="divide-y divide-[hsl(var(--border))] max-h-72 overflow-y-auto">
            {d.payout_history.map((p) => (
              <div key={p.id} className="flex items-center justify-between py-2 text-sm">
                <div><span className="font-semibold">{usd(p.amount)}</span> <span className="text-xs text-[hsl(var(--muted-foreground))]">{p.method}</span></div>
                <Badge variant="secondary" className="capitalize">{p.status}</Badge>
              </div>
            ))}
          </div>
        </Panel>
      )}

      <Dialog open={!!drill} onOpenChange={(v) => !v && setDrill(null)}>
        <DialogContent data-testid="sa-creator-drill">
          <DialogHeader>
            <DialogTitle>{(drill && !drill.loading && drill.creator?.name) || "Creator earnings"}</DialogTitle>
            {drill && !drill.loading && drill.creator?.email && <DialogDescription>{drill.creator.email}</DialogDescription>}
          </DialogHeader>
          {!drill || drill.loading ? <div className="py-8 grid place-items-center"><Spinner /></div> : (
            <>
              <div className="grid grid-cols-3 gap-2 text-center my-2">
                <div className="rounded-xl border border-[hsl(var(--border))] p-2"><div className="text-[11px] text-[hsl(var(--muted-foreground))]">Gross</div><div className="font-bold">{usd(drill.gross)}</div></div>
                <div className="rounded-xl border border-[hsl(var(--border))] p-2"><div className="text-[11px] text-[hsl(var(--muted-foreground))]">Net</div><div className="font-bold">{usd(drill.net)}</div></div>
                <div className="rounded-xl border border-[hsl(var(--border))] p-2"><div className="text-[11px] text-[hsl(var(--muted-foreground))]">Available</div><div className="font-bold text-emerald-500">{usd(drill.available)}</div></div>
              </div>
              <div className="text-xs font-semibold mb-1">By source</div>
              <div className="space-y-1 text-sm">
                {drill.by_kind.map((k) => (<div key={k.kind} className="flex justify-between"><span className="capitalize">{k.kind} <span className="text-[hsl(var(--muted-foreground))]">×{k.count}</span></span><b>{usd(k.total)}</b></div>))}
                {drill.by_kind.length === 0 && <p className="text-[hsl(var(--muted-foreground))]">No earnings recorded.</p>}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}


function OwnerCreate({ navigate }) {
  const [games, setGames] = useState(null);
  useEffect(() => {
    api.get("/games")
      .then((r) => setGames(Array.isArray(r.data) ? r.data : (r.data.items || r.data.games || [])))
      .catch(() => setGames([]));
  }, []);

  return (
    <div className="space-y-6" data-testid="owner-create-panel">
      <div className="rounded-2xl border border-[hsl(var(--primary))]/30 bg-gradient-to-br from-[hsl(var(--primary))]/10 to-transparent p-6">
        <h2 className="font-display text-xl font-bold flex items-center gap-2"><Sparkles className="w-6 h-6 text-[hsl(var(--primary))]" /> Create games — owner mode</h2>
        <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1 max-w-xl">Build and publish games yourself with no credit limits. Switch back to <b>Oversee operation</b> any time to monitor monetisation, subscriptions and the whole platform.</p>
        <div className="flex flex-wrap gap-2 mt-4">
          <Button onClick={() => navigate("/app/create")} className="rounded-full bg-gradient-to-r from-[#1f6feb] to-[#7c3aed]" data-testid="owner-create-new"><Plus className="w-4 h-4" /> Start a new game</Button>
          <Button variant="outline" onClick={() => navigate("/app/games")} className="rounded-full" data-testid="owner-create-mygames"><Gamepad2 className="w-4 h-4" /> All my games</Button>
        </div>
      </div>
      <div>
        <h3 className="font-bold mb-3 flex items-center gap-2"><Gamepad2 className="w-5 h-5" /> Your games</h3>
        {games === null ? <Spinner /> : games.length === 0 ? (
          <p className="text-sm text-[hsl(var(--muted-foreground))]">No games yet — hit “Start a new game” to create your first.</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="owner-games-grid">
            {games.slice(0, 12).map((g) => (
              <div key={g.id} className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4" data-testid={`owner-game-${g.id}`}>
                <div className="flex items-center gap-2"><span className="text-2xl">{g.emoji || g.icon || "🎮"}</span><div className="font-bold truncate">{g.title || "Untitled game"}</div></div>
                <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1 capitalize">{g.status || "draft"}{g.genre ? ` · ${g.genre}` : ""}</div>
                <div className="flex gap-2 mt-3">
                  <Button size="sm" className="rounded-full flex-1" onClick={() => navigate(`/app/build/${g.id}`)} data-testid={`owner-edit-${g.id}`}><Wrench className="w-4 h-4" /> Build</Button>
                  <Button size="sm" variant="outline" className="rounded-full" onClick={() => navigate(`/app/arcade/${g.id}`)} data-testid={`owner-play-${g.id}`}><PlayCircle className="w-4 h-4" /> Play</Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function MoneyCentre() {
  const [data, setData] = useState(null);
  const [trends, setTrends] = useState(null);
  const [gpu, setGpu] = useState(null);
  const [days, setDays] = useState(90);
  useEffect(() => {
    let alive = true;
    setData(null); setTrends(null);
    api.get(`/superadmin/money?days=${days}`).then((r) => alive && setData(r.data)).catch(() => alive && setData({ summary: {}, feed: [] }));
    api.get(`/superadmin/monetization/trends?days=${days}`).then((r) => alive && setTrends(r.data.series)).catch(() => alive && setTrends([]));
    return () => { alive = false; };
  }, [days]);
  useEffect(() => { api.get("/superadmin/cinematic-spend").then((r) => setGpu(r.data)).catch(() => setGpu(null)); }, []);
  if (!data) return <Spinner />;
  const s = data.summary || {};
  const usd = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const exportCsv = () => {
    const rows = [["date", "direction", "type", "label", "who", "detail", "amount_usd"]];
    (data.feed || []).forEach((f) => rows.push([f.date, f.direction, f.type, f.label, f.who, (f.detail || "").replace(/,/g, " "), f.amount]));
    const csv = rows.map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = `gameview-money-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const WINDOWS = [{ d: 30, label: "30d" }, { d: 90, label: "90d" }, { d: 365, label: "365d" }];

  return (
    <div className="space-y-5" data-testid="money-centre">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h3 className="font-display font-bold flex items-center gap-2"><Wallet className="w-5 h-5 text-emerald-500" /> Money — unified revenue feed</h3>
        <div className="flex items-center gap-2">
          <div className="flex rounded-full border border-[hsl(var(--border))] p-0.5" data-testid="money-window-switch">
            {WINDOWS.map((w) => (
              <button key={w.d} onClick={() => setDays(w.d)} data-testid={`money-window-${w.d}`}
                className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${days === w.d ? "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]" : "text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"}`}>{w.label}</button>
            ))}
          </div>
          <Button onClick={exportCsv} variant="outline" className="rounded-full" data-testid="money-export-csv"><Download className="w-4 h-4" /> Export CSV</Button>
        </div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard label="Gross revenue" value={usd(s.gross_revenue)} icon={DollarSign} accent="#22c55e" />
        <StatCard label="Net (after payouts)" value={usd(s.net_revenue)} icon={TrendingUp} accent="#10b981" delay={1} />
        <StatCard label="MRR / ARR" value={`${usd(s.mrr)} / ${usd(s.arr)}`} icon={TrendingUp} accent="#a855f7" delay={2} />
        <StatCard label="Creator payouts" value={usd(s.creator_payouts)} icon={Wallet} accent="#f59e0b" delay={3} />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <StatCard label="Subscription sales" value={usd(s.subscription_revenue)} icon={CreditCard} accent="#3b82f6" />
        <StatCard label="Credit-pack sales" value={usd(s.credit_revenue)} icon={DollarSign} accent="#06b6d4" delay={1} />
        <StatCard label="Creator tips" value={usd(s.tip_revenue)} icon={Heart} accent="#ec4899" delay={2} />
        <StatCard label={`Net last ${s.window_days || days}d`} value={usd(s.net_window)} icon={TrendingUp} accent="#22c55e" delay={3} />
        <StatCard label="GPU spend · est. this month" value={gpu ? usd(gpu.est_spend) : "—"} icon={Clapperboard} accent="#f59e0b" delay={4} />
      </div>
      {gpu && (
        <p className={`text-[11px] -mt-2 ${gpu.over_budget ? "text-amber-500 font-semibold" : "text-[hsl(var(--muted-foreground))]"}`} data-testid="money-gpu-note">
          🎬 {gpu.sessions_month} Cinematic session{gpu.sessions_month === 1 ? "" : "s"} this month × ~{gpu.assumed_min} min @ {usd(gpu.cost_per_hour)}/hr
          {gpu.budget_cap > 0 ? ` · budget ${usd(gpu.est_spend)} / ${usd(gpu.budget_cap)}` : ""}
          {gpu.cost_per_hour === 0 ? " — set a GPU cost/hour in Settings to estimate spend." : `. ${gpu.sessions_total} sessions all-time.`}
          {gpu.over_budget ? " ⚠️ Over budget — Cinematic Mode auto-paused." : ""}
        </p>
      )}
      <Panel title={`Revenue trend — last ${days} days`}>
        {trends === null ? <Spinner /> : <RevenueTrendChart series={trends} />}
      </Panel>
      <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] overflow-hidden">
        <div className="px-4 py-3 border-b border-[hsl(var(--border))] font-semibold text-sm">Recent activity</div>
        {(!data.feed || data.feed.length === 0) ? (
          <p className="p-6 text-sm text-[hsl(var(--muted-foreground))] text-center">No revenue activity yet. Subscriptions, credit-pack purchases and creator payouts will appear here.</p>
        ) : (
          <div className="divide-y divide-[hsl(var(--border))] max-h-[480px] overflow-y-auto" data-testid="money-feed">
            {data.feed.map((f, i) => (
              <div key={i} className="flex items-center gap-3 px-4 py-2.5 text-sm" data-testid={`money-row-${i}`}>
                <Badge variant={f.direction === "in" ? "default" : "secondary"} className={`text-[10px] shrink-0 ${f.direction === "in" ? "bg-emerald-500/90" : "bg-amber-500/90"}`}>{f.label}</Badge>
                <div className="min-w-0 flex-1">
                  <div className="truncate">{f.who}{f.detail ? <span className="text-[hsl(var(--muted-foreground))]"> · {f.detail}</span> : null}</div>
                  <div className="text-[11px] text-[hsl(var(--muted-foreground))]">{(f.date || "").slice(0, 10)}</div>
                </div>
                <div className={`font-mono font-bold tabular-nums shrink-0 ${f.direction === "in" ? "text-emerald-500" : "text-amber-500"}`}>{f.direction === "in" ? "+" : "−"}{usd(f.amount)}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

